import { type ReactElement, type ReactNode, useEffect, useState } from 'react';

/**
 * A native `<details>`/`<summary>` disclosure, not a hand-rolled one — free
 * keyboard support (Enter/Space toggles, native focus handling) and free
 * screen-reader semantics (`aria-expanded` is implicit to the element),
 * with zero new runtime dependency.
 *
 * `open` is still React-controlled rather than left fully uncontrolled,
 * for exactly one reason: `forceOpen`. A field inside a collapsed section
 * (e.g. a malformed SEO URL) must not go missing behind a fold the user
 * never opened — so a validation error in a section's own fields flips it
 * open automatically, once, and stays open afterward even if the error is
 * later fixed (collapsing a section the user has already looked at would
 * be its own kind of surprising).
 */
export function CollapsibleSection({
  title,
  defaultOpen,
  forceOpen,
  badge,
  children,
}: {
  readonly title: string;
  readonly defaultOpen: boolean;
  /** Set true once a field inside this section fails validation. */
  readonly forceOpen: boolean;
  /** e.g. the localized "Optional" pill, shown next to the title. */
  readonly badge?: ReactNode;
  readonly children: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  return (
    <details
      open={open}
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
      }}
      className="group rounded-md border border-line"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 marker:content-none">
        <span className="flex items-center gap-2">
          <span className="font-display text-base font-semibold text-ink">{title}</span>
          {badge}
        </span>
        <ChevronIcon className="text-inkFaint transition-transform group-open:rotate-180" />
      </summary>
      <div className="flex flex-col gap-4 border-t border-line px-4 py-4">{children}</div>
    </details>
  );
}

/** Local to this feature — a section fold is the only place this project needs a chevron. */
function ChevronIcon({ className }: { readonly className?: string }): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
